import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import * as React from "react";
import { NavLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { CHART_NOTE_COMPLETED } from "constants/chart-notes";
import {
  CONFIGURABLE_APPOINTMENT_CHECKLIST,
  NEW_APPOINTMENT_OUTCOME_CHANGES
} from "constants/feature-flags";
import { SALE_DRAFT_STATUS_TYPE } from "constants/sales";
import {
  useGetAppointmentChecklistQuery,
  useUpdateAppointmentChecklistMutation
} from "services/appointment-checklists";
import { useGetChartNotesQuery } from "services/chart-notes";
import { useQuerySalesQuery } from "services/sales";

import { classes } from "../appointment-preview.component";

type AppointmentChecklistProps = {
  appointmentId: number;
  patientId: number;
  hasGeneratedInvoice?: boolean;
  setCanAppointmentBeCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  setHasCustomChecklist?: React.Dispatch<React.SetStateAction<boolean>>;
  sx?: SxProps;
};

const defaultSx = { pt: 0, pl: 1, pr: 1, pb: 0 };

export default function AppointmentChecklist({
  appointmentId,
  patientId,
  hasGeneratedInvoice = false,
  setCanAppointmentBeCompleted,
  setHasCustomChecklist,
  sx = defaultSx
}: AppointmentChecklistProps) {
  const flags = useFlags();
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];
  const configurableChecklistEnabled =
    flags[CONFIGURABLE_APPOINTMENT_CHECKLIST];

  const [chartNoteCompleted, setChartNoteCompleted] = React.useState(false);
  const [saleCompleted, setSaleCompleted] =
    React.useState(!hasGeneratedInvoice);
  const [customItemsCompleted, setCustomItemsCompleted] = React.useState<
    Record<number, boolean>
  >({});
  const [updateAppointmentChecklist] = useUpdateAppointmentChecklistMutation();

  const filters = {
    appointment: appointmentId,
    limit: 1,
    patient: patientId,
    query: "{id,status}"
  };

  const { data: checklistData } = useGetAppointmentChecklistQuery(
    appointmentId,
    { skip: !configurableChecklistEnabled }
  );

  const hasCustomChecklist =
    configurableChecklistEnabled &&
    Boolean(checklistData?.has_custom_checklist);

  const {
    data: sales,
    isLoading: isLoadingSales,
    isSuccess: isSuccessSales
  } = useQuerySalesQuery(filters, {
    skip: !hasGeneratedInvoice || hasCustomChecklist
  });
  const {
    data: chartNotes,
    isLoading: isLoadingChartNotes,
    isSuccess: isSuccessChartNotes
  } = useGetChartNotesQuery(filters, {
    skip: newAppointmentOutcomeChangesEnabled || hasCustomChecklist
  });

  const hasChartNotesLoaded = !isLoadingChartNotes && isSuccessChartNotes;
  const hasSalesLoaded = !isLoadingSales && isSuccessSales;

  useEffect(() => {
    setHasCustomChecklist?.(hasCustomChecklist);
  }, [hasCustomChecklist, setHasCustomChecklist]);

  useEffect(() => {
    if (checklistData?.items) {
      const initial: Record<number, boolean> = {};
      checklistData.items.forEach((item) => {
        initial[item.checklist_item.id] = item.is_completed;
      });
      setCustomItemsCompleted(initial);
    }
  }, [checklistData]);

  useEffect(() => {
    if (hasChartNotesLoaded || newAppointmentOutcomeChangesEnabled) {
      const [chartNote] = chartNotes || [];
      setChartNoteCompleted(
        newAppointmentOutcomeChangesEnabled ||
          chartNote?.status === CHART_NOTE_COMPLETED
      );
    }
    if (hasSalesLoaded) {
      const [sale] = sales || [];
      setSaleCompleted(Boolean(sale?.status !== SALE_DRAFT_STATUS_TYPE));
    }
  }, [
    hasChartNotesLoaded,
    hasSalesLoaded,
    chartNotes,
    sales,
    newAppointmentOutcomeChangesEnabled
  ]);

  useEffect(() => {
    if (hasCustomChecklist && checklistData?.items) {
      const requiredItems = checklistData.items.filter(
        (item) => item.checklist_item.is_required
      );
      const allRequiredChecked =
        requiredItems.length === 0 ||
        requiredItems.every(
          (item) =>
            customItemsCompleted[item.checklist_item.id] ?? item.is_completed
        );
      setCanAppointmentBeCompleted(allRequiredChecked);
    } else {
      setCanAppointmentBeCompleted(
        chartNoteCompleted && (!hasGeneratedInvoice || saleCompleted)
      );
    }
  }, [
    hasCustomChecklist,
    checklistData,
    customItemsCompleted,
    saleCompleted,
    chartNoteCompleted,
    hasGeneratedInvoice,
    setCanAppointmentBeCompleted
  ]);

  return (
    <Box display="flex">
      <FormControl component="fieldset">
        <FormGroup>
          {hasCustomChecklist ? (
            checklistData!.items.map((item) => (
              <FormControlLabel
                key={item.checklist_item.id}
                control={
                  <Checkbox
                    checked={
                      customItemsCompleted[item.checklist_item.id] ??
                      item.is_completed
                    }
                    size="small"
                    sx={sx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const checked = e.target.checked;
                      const newState = {
                        ...customItemsCompleted,
                        [item.checklist_item.id]: checked
                      };
                      setCustomItemsCompleted(newState);
                      updateAppointmentChecklist({
                        appointmentId,
                        items: checklistData!.items.map((i) => ({
                          id: i.id,
                          is_completed:
                            newState[i.checklist_item.id] ?? i.is_completed
                        }))
                      });
                    }}
                  />
                }
                label={
                  <Box component="span">
                    {item.checklist_item.label}
                    {item.checklist_item.is_required && (
                      <Typography
                        component="span"
                        color="error"
                        sx={{ ml: 0.5 }}
                      >
                        *
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))
          ) : (
            <>
              {!newAppointmentOutcomeChangesEnabled && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={chartNoteCompleted}
                      name="appointmentChartNote"
                      size="small"
                      sx={sx}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setChartNoteCompleted(event.target.checked)
                      }
                    />
                  }
                  labelPlacement="start"
                  label={
                    <Box>
                      Is {"  "}
                      <NavLink
                        to={`/patients/${patientId}/chart-notes`}
                        className={classes.text}
                        data-testid="appointment-preview-chart-note"
                      >
                        Chart note
                      </NavLink>
                      {"  "}completed?
                    </Box>
                  }
                />
              )}
              {hasGeneratedInvoice && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saleCompleted}
                      name="appointmentSale"
                      size="small"
                      sx={sx}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setSaleCompleted(event.target.checked)
                      }
                    />
                  }
                  labelPlacement="start"
                  label={
                    <Box>
                      Is{"  "}
                      <NavLink
                        to={`/patients/${patientId}/billing`}
                        className={classes.text}
                        data-testid="appointment-preview-sale"
                      >
                        Invoice
                      </NavLink>
                      {"  "}in Ready to Bill status?
                    </Box>
                  }
                />
              )}
            </>
          )}
        </FormGroup>
      </FormControl>
    </Box>
  );
}
